import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useAeroUploadStore } from '@/stores/aeroUploadStore'
import { UploadStatus } from '@/types/aeroUpload/aeroUpload'
import { useCommonModalStore } from '@/stores/commonModalStore'
import {
  ACCEPTED_FILE_NAME_WITH_EXTENSION_LENGTH,
  isAcceptedExtension
} from '@/constants/ui/aeroUpload/uploadAccepts'

export const usePartsCombination = () => {
  const isDragOver = ref(false)
  const fileInput = ref<HTMLInputElement | null>(null)
  const uploadStore = useAeroUploadStore()
  const { isUploading, uploadFileList } = storeToRefs(uploadStore)
  const commonModalStore = useCommonModalStore()

  const uploadButtonDisabled = computed(
    () =>
      isUploading.value ||
      uploadFileList.value.filter(d => d.status !== UploadStatus.COMPLETE).length === 0
  )

  const cancelButtonDisabled = computed(() => uploadFileList.value.length === 0)

  const dragOver = () => (isDragOver.value = true)

  const dragLeave = () => (isDragOver.value = false)

  const dropFile = (event: DragEvent) => {
    event.preventDefault()
    isDragOver.value = false
    if (event.dataTransfer) {
      const droppedFiles = Array.from(event.dataTransfer.files)
      const zipFiles = droppedFiles.filter(file => file.name.toLowerCase().endsWith('.zip'))
      if (zipFiles.length === 0) {
        // zipfile以外はreturn
        return
      }
      if (checkFileNameLength(zipFiles)) {
        return
      }
      // ファイルリストに追加
      uploadStore.addFiles(...zipFiles)
    }
  }

  /**
   * ドロップエリアクリック時にファイルインプットイベントを起こす
   */
  const onClickArea = () => {
    if (fileInput.value) {
      fileInput.value.click()
    }
  }

  const onFileChange = (event: Event) => {
    const input = event.target as HTMLInputElement
    if (!input.files) return
    const droppedFiles = Array.from(input.files)
    const zipFiles = droppedFiles.filter(file => isAcceptedExtension(file.name))
    if (zipFiles.length === 0) {
      // zipfile以外はreturn
      return
    }
    if (checkFileNameLength(zipFiles)) {
      return
    }
    // ファイルリストに追加
    uploadStore.addFiles(...zipFiles)
    input.value = ''
  }

  /**
   * ファイル名の長さをチェック
   * @param files
   * @returns
   */
  const checkFileNameLength = (files: File[]): boolean => {
    for (const file of files) {
      if (file.name.length > ACCEPTED_FILE_NAME_WITH_EXTENSION_LENGTH) {
        // 拡張子を除いたファイル名の長さ
        const file_name_length = ACCEPTED_FILE_NAME_WITH_EXTENSION_LENGTH - 4
        // アップロード確認ダイアログ表示
        commonModalStore.openAlertModal({
          body:
            'アップロードを行う空撮画像のファイル名は拡張子を除いて' +
            file_name_length +
            '字以内にしてください。'
        })
        return true
      }
    }
    return false
  }

  /** アップロードボタン押下時処理 */
  const upload = async () => {
    await uploadStore.executeUpload()
  }

  /** キャンセルボタン押下時処理 */
  const cancel = async () => {
    if (isUploading.value) {
      // アップロード中の場合はアップロードを中断
      const res = await commonModalStore.openConfirmModalAsync({
        body: 'アップロードをキャンセルします。よろしいですか？'
      })
      if (res) {
        uploadStore.abortUpload()
      }
    } else {
      // アップロード中でなければファイルリストをクリア
      const res = await commonModalStore.openConfirmModalAsync({
        body: '一覧表をクリアします。よろしいですか？'
      })
      if (res) {
        uploadStore.clearFiles()
      }
    }
  }

  return {
    isDragOver,
    fileInput,
    isUploading,
    uploadButtonDisabled,
    cancelButtonDisabled,
    dragOver,
    dragLeave,
    dropFile,
    onClickArea,
    onFileChange,
    upload,
    cancel
  }
}
